/**
 * Marca de ORIGEM da coordenada de entrega — de onde o pino NASCEU, se o cliente o corrigiu e quanto
 * ele andou. Vai no pedido (`details.coordinatesOrigin`, gravado pelo server em
 * `orders.coordinates_source / _corrected / _moved_m`) e alimenta a tabela de observações do pino.
 *
 * A tela grava a marca no aparelho junto da coordenada (`coordinate_origin`, com versão e origem — regra
 * de todo dado novo no storage) e o checkout a lê de volta. A marca é PRESA à coordenada: ela guarda o
 * ponto confirmado e só vai no pedido se esse ponto for o mesmo que está saindo. Qualquer caminho que
 * troque a coordenada sem passar pela tela deixa a marca órfã, e marca órfã não vai.
 *
 * Vocabulário de `src` (o de `coordinate_source`, mais os passos novos da cascata do pino grátis):
 *   ja_confirmado · cnefe · gps · photon_rua · here · geocode (Google) · trusted-cache · bairro (descartado)
 */
export const COORD_ORIGIN_KEY = 'coordinate_origin';
export const COORD_ORIGIN_VERSION = 1;

/** Precisão com que o ponto confirmado é comparado ao que sai no pedido (~1 m). */
const SAME_POINT_DEG = 1e-5;

export interface CoordOrigin {
    v: number;
    /** de onde veio o ponto em que o pino nasceu (não muda com o arraste) */
    src: string;
    /** true = arrasto deliberado; false = confirmou onde nasceu; null = não houve mapa */
    corrected: boolean | null;
    moved_m: number | null;
    init_lat: number | null;
    init_lng: number | null;
    /** o ponto confirmado — a marca só vale para ele */
    lat: number;
    lng: number;
    at: number;
}

/** O que vai no pedido (o server confere o formato em Coordinates\CoordinatesOrigin). */
export interface CoordOriginPayload {
    v: number;
    source: string;
    corrected: boolean | null;
    movedM: number | null;
    initialLat: number | null;
    initialLng: number | null;
}

interface Storage {
    getItem(k: string): any;
    setItem(k: string, v: string): any;
    removeItem(k: string): any;
}

const num = (x: any): number | null => {
    if (x === null || x === undefined || x === '') return null;
    const n = Number(x);
    return Number.isFinite(n) ? n : null;
};

/**
 * Monta a marca. Sem origem ou sem ponto confirmado válido devolve null (não há o que marcar).
 * `corrected` null quando o mapa não foi mostrado (ponto reaproveitado, confirmação sem mapa).
 */
export function buildCoordOrigin(p: {
    src: string | null | undefined;
    corrected: boolean | null;
    movedM: number | null | undefined;
    initLat: any;
    initLng: any;
    lat: any;
    lng: any;
    now?: number;
}): CoordOrigin | null {
    const src = typeof p.src === 'string' ? p.src.trim() : '';
    const lat = num(p.lat);
    const lng = num(p.lng);
    if (!src || lat == null || lng == null || (lat === 0 && lng === 0)) return null;
    const moved = num(p.movedM);
    return {
        v: COORD_ORIGIN_VERSION,
        src,
        corrected: typeof p.corrected === 'boolean' ? p.corrected : null,
        moved_m: moved != null && moved >= 0 ? Math.round(moved) : null,
        init_lat: num(p.initLat),
        init_lng: num(p.initLng),
        lat,
        lng,
        at: p.now ?? Date.now(),
    };
}

export function saveCoordOrigin(storage: Storage, origin: CoordOrigin | null): void {
    try {
        if (origin) storage.setItem(COORD_ORIGIN_KEY, JSON.stringify(origin));
        else storage.removeItem(COORD_ORIGIN_KEY);
    } catch (e) {
        // storage cheio/bloqueado: o pedido segue sem marca
    }
}

/** Lê a marca gravada; versão desconhecida ou JSON quebrado = sem marca. */
export function readCoordOrigin(storage: Storage): CoordOrigin | null {
    try {
        const raw = storage.getItem(COORD_ORIGIN_KEY);
        if (!raw) return null;
        const o = JSON.parse(raw);
        if (!o || o.v !== COORD_ORIGIN_VERSION || typeof o.src !== 'string' || !o.src) return null;
        if (num(o.lat) == null || num(o.lng) == null) return null;
        return o as CoordOrigin;
    } catch (e) {
        return null;
    }
}

/**
 * A marca para o pedido que sai com `coordinates`, ou null quando ela não é DESTA coordenada
 * (órfã: o ponto mudou por outro caminho, ou não há ponto no pedido).
 */
export function coordOriginForOrder(storage: Storage, coordinates: { latitude?: any; longitude?: any } | null | undefined): CoordOriginPayload | null {
    const o = readCoordOrigin(storage);
    const lat = num(coordinates?.latitude);
    const lng = num(coordinates?.longitude);
    if (!o || lat == null || lng == null) return null;
    if (Math.abs(o.lat - lat) > SAME_POINT_DEG || Math.abs(o.lng - lng) > SAME_POINT_DEG) return null;
    return {
        v: o.v,
        source: o.src,
        corrected: o.corrected,
        movedM: o.moved_m,
        initialLat: o.init_lat,
        initialLng: o.init_lng,
    };
}
